import React, { useState, useRef, useEffect } from 'react';
import { getAuthHeaders } from '../supabaseClient';
import Icon from './Icon';
import SessionNotice from './SessionNotice';
import { computePotential, potentialAmountText } from './heroSelection';
import PrivacyNotice from './PrivacyNotice';

const HISTORY_URL = 'https://tm-audit-platform-production.up.railway.app/api/audit-history';

// Whole-dollar money for the home screen (cleaner than the cents history shows).
// The VALUES are the exact aggregates history computes — only the formatting differs.
const money = (n) => n != null ? '$' + Math.round(parseFloat(n)).toLocaleString('en-US') : '—';

const fmtWhen = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const startOfDay = (dt) => { const x = new Date(dt); x.setHours(0, 0, 0, 0); return x; };
    const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
    if (diffDays === 0) return 'Today, ' + time;
    if (diffDays === 1) return 'Yesterday, ' + time;
    if (diffDays < 7) return diffDays + ' days ago, ' + time;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function UploadScreen({ onSubmit, onHistory, onAdmin, onReconcile, onReopen, isAdmin, error, sessionNotice, onTakeOverSession, subscription, onShowPricing, session }) {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [toast, setToast] = useState(null);
    const fileRef = useRef();

    // Recent audits + portfolio aggregates — SAME endpoint and shape HistoryScreen
    // uses. No new query, no new aggregation: verified/likely and the portfolio
    // totals are read straight off the response, exactly as history reads them.
    const [recent, setRecent] = useState([]);
    const [totalValueTiers, setTotalValueTiers] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);

    useEffect(() => {
        if (!session) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(HISTORY_URL, { headers: await getAuthHeaders() });
                const json = await res.json();
                if (cancelled) return;
                const rows = (json.rows || []).filter(a => a.created_at);
                setRecent(rows.slice(0, 5));
                setTotalValueTiers(json.total_value_tiers ?? null);
                setTotalCount(json.total_count ?? rows.length);
            } catch (e) {
                // Preview origins can't reach the backend (CORS); the screen still
                // works for uploading — recent audits just stay empty.
                console.warn('Failed to load recent audits:', e);
            } finally {
                if (!cancelled) setHistoryLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [session]);

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(null), 3500);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files[0];
        if (dropped && dropped.type === "application/pdf") {
            setFile(dropped);
        } else if (dropped) {
            showToast("Only PDF files are accepted.");
        }
        setDragging(false);
    };

    const handleFileInput = (e) => {
        const selected = e.target.files[0];
        if (selected && selected.type === "application/pdf") {
            setFile(selected);
        } else if (selected) {
            showToast("Only PDF files are accepted.");
        }
    };

    // POTENTIAL-FIRST (Decision Log 2026-08-13). Mirrors the report hero and the
    // history stat bar: the bounded POTENTIAL — Verified + Likely + Conditional —
    // so the portfolio figure is never smaller than the reports composing it.
    // Always a floor ("+"): a portfolio sum spans many estimates, so there is no
    // single grand total to bound against. Fails closed to '—' when the backend
    // returns no graded tiers — never the legacy ungated total_dollar_impact,
    // which once read $11.8M portfolio-wide.
    const totalPotential = totalValueTiers ? potentialAmountText(computePotential({
        verifiedTotal: totalValueTiers.verified,
        likelyTotal: totalValueTiers.likely,
        conditionalTotal: totalValueTiers.conditional,
        conditionalPrecise: false,
    }), money) : money(null);

    return (
        <div className="home-screen">
            {toast && (
                <div className="home-toast">{toast}</div>
            )}

            <header className="home-brand">
                <span className="logo-audee">Audee<span className="logo-dot">.</span></span>
            </header>

            {subscription && ['trial', 'trial_expired', 'trial_cap_reached'].includes(subscription.access_reason) && (
                <div className="trial-banner" onClick={onShowPricing}>
                    {subscription.access_reason === 'trial' ? (
                        <>
                            <span className="trial-banner-text">Free trial: {subscription.days_left} day{subscription.days_left !== 1 ? 's' : ''} left</span>
                            <span className="trial-banner-cta">View plans <Icon name="arrow-right" size={13} /></span>
                        </>
                    ) : (
                        <>
                            <span className="trial-banner-text">{subscription.access_reason === 'trial_cap_reached' ? 'Free trial audit limit reached' : 'Free trial ended'}</span>
                            <span className="trial-banner-cta">Subscribe to continue <Icon name="arrow-right" size={13} /></span>
                        </>
                    )}
                </div>
            )}

            <div className="home-body">
                <div className="home-heading">
                    <h1 className="home-title">Run An Audit</h1>
                    <p className="home-sub">Drop an Xactimate estimate or T&amp;M bill. Type is detected automatically.</p>
                </div>

                <div
                    className={"home-dropzone" + (dragging ? " dragging" : "") + (file ? " has-file" : "")}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current.click()}
                >
                    <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={handleFileInput} />
                    <div className="home-dropzone-icon">
                        <Icon name={file ? "check-circle" : "upload"} size={40} />
                    </div>
                    {file ? (
                        <div className="home-dropzone-file">{file.name}</div>
                    ) : (
                        <>
                            <div className="home-dropzone-label">Drop Your PDF Here</div>
                            <div className="home-dropzone-hint">or click to browse</div>
                        </>
                    )}
                    <button
                        className="home-run-btn"
                        onClick={(e) => { e.stopPropagation(); if (file) onSubmit(file); }}
                        disabled={!file}
                    >
                        Run Audit
                    </button>
                </div>

                {/* One hero stat, not two. The audits count was previously a second
                    card of equal weight, which gave a raw counter the same billing as
                    the money figure and read as filler. It is now a caption that
                    QUALIFIES the total ("across N audits") rather than competing with
                    it. The caption slot renders a skeleton while loading so the card
                    is the same height before and after data lands. */}
                <div className="home-metrics">
                    <div className="home-metric">
                        <div className="home-metric-value money">
                            {historyLoaded ? totalPotential : <span className="home-metric-skeleton" aria-hidden="true" />}
                        </div>
                        <div className="home-metric-label">Total potential identified</div>
                        <div className="home-metric-caption">
                            {historyLoaded
                                ? `across ${(totalCount ?? 0).toLocaleString('en-US')} audit${totalCount === 1 ? '' : 's'}`
                                : <span className="home-skeleton-bar" style={{ width: 96, height: 10 }} aria-hidden="true" />}
                        </div>
                    </div>
                </div>

                {sessionNotice && (
                    <SessionNotice kind="conflict" onTakeOver={onTakeOverSession} busy={sessionNotice.busy} failed={sessionNotice.failed} />
                )}
                {error && <div className="upload-error"><Icon name="alert-triangle" size={16} /> {error}</div>}

                {/* While the history fetch is in flight, reserve the Recent-audits
                    region with a quiet skeleton instead of leaving it absent. This
                    is what stops the content area from jumping when data lands: the
                    region occupies space throughout, rather than appearing from
                    nothing. Neutral by design — no row here asserts "you have
                    audits"; the real list vs. the real empty state is only decided
                    once historyLoaded is true (same fail-closed rule as the metrics
                    and the tiers: a loading state must never read as a real one). */}
                {!historyLoaded && (
                    <section className="home-recent" aria-hidden="true">
                        <div className="home-recent-head">
                            <span className="home-skeleton-bar" style={{ width: 104, height: 15 }} />
                        </div>
                        <div className="home-recent-card">
                            {[0, 1, 2].map(i => (
                                <div key={i} className="home-recent-row home-recent-row-skeleton">
                                    <div className="home-recent-left">
                                        <span className="home-skeleton-bar" style={{ width: '55%', height: 14 }} />
                                        <span className="home-skeleton-bar" style={{ width: '38%', height: 10, marginTop: 6 }} />
                                    </div>
                                    <div className="home-recent-right">
                                        <span className="home-skeleton-bar" style={{ width: 70, height: 16, marginLeft: 'auto' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {historyLoaded && recent.length > 0 && (
                    <section className="home-recent">
                        <div className="home-recent-head">
                            <h2 className="home-recent-title">Recent audits</h2>
                            <button className="home-viewall" onClick={onHistory}>View all <Icon name="arrow-right" size={12} /></button>
                        </div>
                        <div className="home-recent-card">
                            {recent.map(audit => {
                                // Row headline = the same bounded POTENTIAL the report hero
                                // shows for this job, so a row can never contradict the report
                                // it opens. Fail closed: with no value_tiers we show '—'
                                // (MISSING data), never the legacy ungated dollar_impact —
                                // that figure predates every grounding/precision gate. The
                                // em-dash is only for missing data or a genuinely empty audit;
                                // a row with findings but no summable dollars leads with the
                                // COUNT, never a dash and never $0.
                                const tiers = audit.value_tiers;
                                const verified = tiers ? (tiers.verified || 0) : 0;
                                const findingsCount = audit.findings_count || 0;
                                const potential = tiers ? computePotential({
                                    verifiedTotal: verified,
                                    likelyTotal: tiers.likely,
                                    conditionalTotal: tiers.conditional,
                                    grandTotal: audit.grand_total,
                                    auditId: audit.id,
                                    conditionalPrecise: false,
                                }) : null;
                                const metaParts = [fmtWhen(audit.created_at)];
                                if (audit.grand_total) metaParts.push(`${money(audit.grand_total)} estimate`);
                                // Not read (GAP-089): no count in the meta line and no dollar
                                // or "no findings" on the right — none of its checks ran.
                                const notRead = !!audit.read_quality?.underRead;
                                if (!notRead && audit.findings_count != null) metaParts.push(`${audit.findings_count} findings`);
                                let right;
                                if (notRead) {
                                    right = (<>
                                        <div className="home-recent-dash">Not read</div>
                                        <div className="home-recent-submeta">no audit performed</div>
                                    </>);
                                } else if (!potential) {
                                    right = (<>
                                        <div className="home-recent-dash">—</div>
                                        <div className="home-recent-submeta">not graded</div>
                                    </>);
                                } else if (potential.total > 0) {
                                    right = (<>
                                        <div className={'home-recent-money' + (verified > 0 ? '' : ' likely')}>
                                            {potentialAmountText(potential, money)}
                                        </div>
                                        <div className="home-recent-submeta">
                                            {verified > 0 ? `incl. ${money(verified)} bill-now` : 'potential — confirm rates'}
                                        </div>
                                    </>);
                                } else if (findingsCount > 0) {
                                    right = (<>
                                        <div className="home-recent-money">{findingsCount} found</div>
                                        <div className="home-recent-submeta">pending validation</div>
                                    </>);
                                } else {
                                    right = (<>
                                        <div className="home-recent-dash">—</div>
                                        <div className="home-recent-submeta">no findings</div>
                                    </>);
                                }
                                return (
                                    <div key={audit.id} className="home-recent-row" onClick={() => onReopen(audit.id)}>
                                        <div className="home-recent-left">
                                            <div className="home-recent-name">{audit.job_name}</div>
                                            <div className="home-recent-meta">{metaParts.join(' · ')}</div>
                                        </div>
                                        <div className="home-recent-right">
                                            {right}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
                {historyLoaded && recent.length === 0 && (
                    <p className="home-empty">Your audits will appear here.</p>
                )}
            </div>

            <div className="upload-footer-links">
                <button className="history-btn" onClick={onHistory}>View audit history</button>
                {/* HIDDEN: reconciliation button — uncomment to restore
                <span className="upload-footer-divider">·</span>
                <button className="history-btn" onClick={onReconcile}>Reconciliation</button>
                */}
                {/* Operators are uploading their clients' estimates. Whatever the
                    platform does with those files should be one click from the place
                    they upload them, not buried. Added 2026-08-26. */}
                <span className="upload-footer-divider">·</span>
                <button className="history-btn" onClick={() => setShowPrivacy(true)}>
                    Privacy &amp; data handling
                </button>
                {isAdmin && <span className="upload-footer-divider">·</span>}
                {isAdmin && <button className="history-btn" onClick={onAdmin}>Admin</button>}
            </div>

            {showPrivacy && <PrivacyNotice onClose={() => setShowPrivacy(false)} />}
        </div>
    );
}

export default UploadScreen;
